import {
  ActiveUsersByDay,
  ActiveUsersTenantBreakdown,
} from "../../models/activeUsersMetrics";
import {
  DEFAULT_SINGLE_SERIES_COLOR,
  OTHER_TENANTS_COLOR,
  OTHER_TENANTS_LABEL,
  TENANT_SERIES_COLORS,
  dayChartInteraction,
  dayChartScales,
  dayLineDataset,
  formatDayLabel,
  tenantDayLegend,
  tenantSeriesColor,
} from "./benefitsMetricsPresentation";
import {
  PLATFORM_DEFAULT_BRAND_COLOR,
  getTenantBrandColor,
} from "./tenantThemeColor";

export interface ActiveUsersTenantSeries {
  key: string;
  label: string;
  color: string;
  activeUsers: number;
  activeUsersByDay: ActiveUsersByDay[];
}

const tenantLabel = (tenant: ActiveUsersTenantBreakdown): string =>
  tenant.tenant_name || tenant.tenant_id;

const mergeActiveUsersByDay = (series: ActiveUsersByDay[][]): ActiveUsersByDay[] => {
  const totals = new Map<string, number>();
  series.forEach((days) => {
    days.forEach((day) => {
      totals.set(day.date, (totals.get(day.date) ?? 0) + day.active_users);
    });
  });
  return Array.from(totals.entries())
    .map(([date, active_users]) => ({ date, active_users }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Series para la vista "Todos".
 *
 * Cada línea toma el color primario del tenant, así el gráfico usa la misma
 * identidad que el resto del BO en vez de un color arbitrario por posición.
 * Se cae a la paleta categórica en dos casos: cuando el tenant no tiene un
 * primary usable, y cuando el color ya lo usó otro tenant — dos líneas del
 * mismo color serían indistinguibles, que es peor que un color arbitrario.
 *
 * Mismo criterio que métricas de beneficios, incluido el bucket "Otros" a
 * partir del noveno tenant con datos.
 */
export const buildActiveUsersTenantSeries = (
  byTenant: ActiveUsersTenantBreakdown[] | null | undefined
): ActiveUsersTenantSeries[] => {
  const withData = (byTenant ?? [])
    .map((tenant, index) => ({ tenant, index }))
    .filter((item) => item.tenant.active_users > 0);

  const named = withData.slice(0, TENANT_SERIES_COLORS.length);
  const overflow = withData.slice(TENANT_SERIES_COLORS.length);

  const usados = new Set<string>();
  const colorDe = (theme: string | null, index: number): string => {
    // El theme del tenant viaja en el propio breakdown, así que no hace falta
    // cablear el hook de colores hasta acá.
    const resuelto = getTenantBrandColor(theme).toLowerCase();
    // getTenantBrandColor nunca devuelve null: sin theme usable cae al default
    // de plataforma. Ese caso cuenta como "sin primary" y va a la paleta, que
    // es lo que evita que todos los tenants sin theme salgan del mismo color.
    const marca = resuelto === PLATFORM_DEFAULT_BRAND_COLOR ? null : resuelto;
    if (marca && !usados.has(marca)) {
      usados.add(marca);
      return marca;
    }
    const fallback = tenantSeriesColor(index);
    usados.add(fallback.toLowerCase());
    return fallback;
  };

  const series: ActiveUsersTenantSeries[] = named.map(({ tenant, index }) => ({
    key: tenant.tenant_id,
    label: tenantLabel(tenant),
    color: colorDe(tenant.tenant_theme, index),
    activeUsers: tenant.active_users,
    activeUsersByDay: tenant.active_users_by_day ?? [],
  }));

  if (overflow.length === 0) {
    return series;
  }

  return [
    ...series,
    {
      key: OTHER_TENANTS_LABEL,
      label: `${OTHER_TENANTS_LABEL} (${overflow.length})`,
      color: OTHER_TENANTS_COLOR,
      activeUsers: overflow.reduce((total, item) => total + item.tenant.active_users, 0),
      activeUsersByDay: mergeActiveUsersByDay(
        overflow.map((item) => item.tenant.active_users_by_day ?? [])
      ),
    },
  ];
};

export const hasActiveUsersTenantBreakdown = (
  series: ActiveUsersTenantSeries[]
): boolean => series.length > 0;

const formatActiveUsersTooltip = (value: number | null): string => {
  const count = value ?? 0;
  return ` ${count} ${count === 1 ? "usuario activo" : "usuarios activos"}`;
};

const formatTenantActiveUsersTooltip = (label: string, value: number | null): string => {
  const count = value ?? 0;
  return ` ${label}: ${count}`;
};

interface TenantDayTooltipItem {
  dataset: { label?: string };
  parsed: { y: number | null };
}

export const activeUsersChartOptions = {
  maintainAspectRatio: false,
  layout: {
    padding: {
      bottom: 8,
    },
  },
  interaction: dayChartInteraction,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
      mode: "point" as const,
      intersect: true,
      callbacks: {
        label: (context: { parsed: { y: number | null } }) =>
          formatActiveUsersTooltip(context.parsed.y),
      },
    },
  },
  scales: dayChartScales,
};

export const activeUsersTenantChartOptions = {
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 8,
      bottom: 8,
    },
  },
  interaction: dayChartInteraction,
  plugins: {
    legend: tenantDayLegend,
    tooltip: {
      enabled: true,
      mode: "point" as const,
      intersect: true,
      callbacks: {
        label: (context: TenantDayTooltipItem) =>
          formatTenantActiveUsersTooltip(context.dataset.label ?? "", context.parsed.y),
      },
    },
  },
  scales: dayChartScales,
};

/**
 * El backend ya devuelve un punto por cada día de la ventana, rellenado con 0: acá no hace
 * falta el relleno de bordes que sí necesitan los canjes de beneficios.
 */
export const buildActiveUsersByDayChartData = (
  activeUsersByDay: ActiveUsersByDay[] | null | undefined,
  seriesColor?: string | null
) => {
  const days = activeUsersByDay ?? [];
  return {
    labels: days.map((item) => formatDayLabel(item.date)),
    datasets: [
      dayLineDataset(
        "Usuarios activos",
        seriesColor || DEFAULT_SINGLE_SERIES_COLOR,
        days.map((item) => item.active_users)
      ),
    ],
  };
};

export const buildActiveUsersTenantChartData = (series: ActiveUsersTenantSeries[]) => {
  const dates = Array.from(
    new Set(series.flatMap((item) => item.activeUsersByDay.map((day) => day.date)))
  ).sort((a, b) => a.localeCompare(b));

  const countsFor = (item: ActiveUsersTenantSeries) => {
    const countsByDate = new Map(
      item.activeUsersByDay.map((day) => [day.date, day.active_users])
    );
    return dates.map((date) => countsByDate.get(date) ?? 0);
  };

  return {
    labels: dates.map(formatDayLabel),
    datasets: series.map((item) => dayLineDataset(item.label, item.color, countsFor(item))),
  };
};
